import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { usePlaceContext } from '../itinerary/PlaceContext';
import pDebounce from 'p-debounce';
import useApi from '../../Api';
import { useAuth } from '../../AuthContext';
import { MapContext } from '../maps/MapContext';
import usePlaceDetailsWrapper from './usePlaceDetailsWrapper';

const useGoogleMap = (
  googleMapInstance,
  placesService,
  location,
  initialZoom = 13,
  pageName = 'default',
  enablePlaceClicks = true
) => {
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 5;
  const { showPlaceInfoBox, hidePlaceInfoBox, setExploreOriginId } = usePlaceContext();
  const apiClient = useApi();
  const { token } = useAuth();
  const { googleMapsAPI } = useContext(MapContext);
  const { fetchPlaceDetailsWrapper } = usePlaceDetailsWrapper();

  const matchToOrigin = useCallback(
    pDebounce(async (name, latitude, longitude, country) => {
      try {
        const response = await apiClient.post(
          '/explore/match_to_origin',
          { name, latitude, longitude, country },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data.origin_data.origin_id;
      } catch {
        return null;
      }
    }, 200),
    [apiClient, token]
  );

  const fetchPlaceId = useCallback((placesService, suggestion) => {
    return new Promise(resolve => {
      const { suggestion_name: name, suggestion_latitude: lat, suggestion_longitude: lng } = suggestion;
      const request = {
        query: name,
        fields: ['place_id'],
        locationBias: { radius: 50, center: new window.google.maps.LatLng(lat, lng) }
      };
      placesService.findPlaceFromQuery(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
          resolve(results[0].place_id);
        } else {
          resolve(null);
        }
      });
    });
  }, []);

  const initializeMap = useCallback(() => {
    if (!googleMapInstance) return;
    if (!location || !location.lat || !location.lng) return;
    try {
      googleMapInstance.setCenter({ lat: location.lat, lng: location.lng });
      googleMapInstance.setZoom(initialZoom);
      let listener = null;
      if (enablePlaceClicks) {
        listener = googleMapInstance.addListener('click', async e => {
          hidePlaceInfoBox();
          if (!placesService) return;
          if (e.placeId) {
            e.stop();
            const placeDetails = await fetchPlaceDetailsWrapper(e.placeId);
            if (placeDetails) {
              if (pageName === 'ExplorePage') {
                const { name, latitude, longitude, country } = placeDetails;
                setExploreOriginId(null);
                const originId = await matchToOrigin(name, latitude, longitude, country);
                if (originId) {
                  setExploreOriginId(originId);
                  placeDetails.origin_id = originId;
                  showPlaceInfoBox(placeDetails);
                }
              } else {
                showPlaceInfoBox(placeDetails);
              }
            }
          }
        });
      }
      return () => {
        if (listener && listener.remove) listener.remove();
      };
    } catch {
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        setTimeout(initializeMap, 500);
      }
    }
  }, [
    googleMapInstance,
    location,
    initialZoom,
    placesService,
    hidePlaceInfoBox,
    fetchPlaceDetailsWrapper,
    showPlaceInfoBox,
    setExploreOriginId,
    matchToOrigin,
    pageName,
    enablePlaceClicks
  ]);

  useEffect(() => {
    if (googleMapInstance && retryCountRef.current === 0) {
      initializeMap();
    }
  }, [googleMapInstance, initializeMap]);

  return {
    googleMap: googleMapInstance,
    placesService,
    fetchPlaceDetailsWrapper,
    fetchPlaceId,
    matchToOrigin
  };
};

export default useGoogleMap;
